import { useEffect, useRef } from 'react'
import { Icon } from './Icon'
import type { IconName } from './Icon'

interface ModalProps {
  title: string
  icon?: IconName
  onClose: () => void
  footer?: React.ReactNode
  children: React.ReactNode
  large?: boolean
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Modal({ title, icon, onClose, footer, children, large = false }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus management: remember the opener, move focus into the dialog,
    // trap Tab inside it, and restore focus when the dialog closes.
    const opener = document.activeElement as HTMLElement | null
    const dialog = dialogRef.current
    const first = dialog?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? dialog)?.focus()

    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && dialog) {
        const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
          (el) => el.offsetParent !== null
        )
        if (items.length === 0) return
        const firstEl = items[0]
        const lastEl = items[items.length - 1]
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault()
          lastEl.focus()
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault()
          firstEl.focus()
        }
      }
    }
    window.addEventListener('keydown', h)
    return () => {
      window.removeEventListener('keydown', h)
      opener?.focus()
    }
  }, [onClose])

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        ref={dialogRef}
        className={large ? 'modal modal-lg' : 'modal'}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div className="modal-head">
          {icon && <Icon name={icon} size={20} style={{ color: 'var(--navy)' }} />}
          <h3 id="modal-title">{title}</h3>
          <button
            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-3)' }}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}
